import avatarEmail from '@/assets/images/email-icon.png';
import avatarSMS from '@/assets/images/sms-icon.png';
import { Loader2Icon, ReplyIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import TooltipComponent from '@/components/common/tooltip/tooltip';

import CopilotPredictionButton from '../copilot/CopilotPredictionButton';
import ActivityLogMessage from './Message/ActivityLogMessage';
import CollapsibleEmailMessage from './Message/CollapsibleEmailMessage';
import MessageComponent from './Message/MessageComponent';

// Component to hide accordion arrow
const NullIcon = () => null;

// MergedMessageList - Renders a mixed list of email threads and regular messages
export default function MergedMessageList({
  messages,
  fullname,
  profile_picture_url,
  defaultPic,
  getOwnerDetails,
  hoveredMessageId,
  setHoveredMessageId,
  owners,
  currentUser,
  onReplyToThread,
  fbFetchLoading,
  fetchFromIG,
  load_fb_past_messages,
  page,
}) {
  if (messages.length === 0) {
    return null;
  }

  // Render a regular message item (non-thread)
  const renderRegularMessage = (message, index, allMessages) => {
    const sameUserAsPrevious =
      index > 0 &&
      !allMessages[index - 1].thread_id &&
      allMessages[index - 1].user_id === message.user_id;

    const lastSentMessage = allMessages
      .filter((item) => !item.thread_id)
      .slice()
      .reverse()
      .find((msg) => msg.direction === 'sent');

    const isLastSentMessage =
      lastSentMessage && lastSentMessage._id === message._id;

    const isNewDay =
      index > 0 &&
      new Date(allMessages[index - 1].sort_timestamp).toLocaleDateString() !==
        new Date(message.sort_timestamp).toLocaleDateString();

    const isWithinLast5Days =
      new Date() - new Date(message.sort_timestamp) < 5 * 24 * 60 * 60 * 1000;

    const timestampMessage =
      isNewDay && index > 0 ? (
        <div
          key={`timestamp_${message.sort_timestamp}`}
          className='my-4 text-center text-xs text-muted-foreground'
        >
          {isWithinLast5Days
            ? `${new Date(message.sort_timestamp).toLocaleDateString('en-US', {
                weekday: 'short',
              })} ${new Date(message.sort_timestamp).toLocaleTimeString(
                'en-US',
                {
                  hour: '2-digit',
                  minute: '2-digit',
                }
              )}`
            : `${new Date(message.sort_timestamp).toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
              })} at ${new Date(message.sort_timestamp).toLocaleTimeString(
                'en-US',
                {
                  hour: '2-digit',
                  minute: '2-digit',
                }
              )}`}
        </div>
      ) : null;

    return (
      <div
        className='group/copilot relative whitespace-pre-wrap break-words'
        style={{ overflowWrap: 'anywhere' }}
        key={message._id}
        onMouseEnter={() => setHoveredMessageId(message._id)}
        onMouseLeave={() => setHoveredMessageId(null)}
      >
        {timestampMessage}
        <div
          className={cn(
            'mx-4 flex items-end space-x-2',
            message.direction === 'sent' ? 'ml-auto' : 'mr-auto',
            sameUserAsPrevious
              ? message.emoji
                ? 'mb-5 mt-3'
                : 'my-3'
              : 'my-3',
            index === messages.length - 1 ? 'mb-6' : ''
          )}
        >
          {message.direction !== 'sent' && (
            <TooltipComponent
              content={<span>{fullname}</span>}
              delayDuration='100'
            >
              <div>
                <Avatar>
                  {profile_picture_url ? (
                    <AvatarImage src={profile_picture_url} />
                  ) : fullname?.[0] ? (
                    <AvatarFallback>
                      {fullname?.charAt(0)?.toUpperCase() || ''}
                    </AvatarFallback>
                  ) : (
                    <AvatarImage src={defaultPic} />
                  )}
                </Avatar>
              </div>
            </TooltipComponent>
          )}
          <TooltipComponent
            content={
              <span>
                {new Date(message.timestamp).toLocaleString('en-US')}
              </span>
            }
            delayDuration='100'
          >
            <div
              className={cn(
                'group relative flex max-w-[75%] flex-col gap-2 self-center whitespace-pre-wrap',
                message.direction === 'sent' ? 'ml-auto' : ''
              )}
            >
              <MessageComponent
                message={message}
                isLastSentMessage={isLastSentMessage}
              />
            </div>
          </TooltipComponent>
          {hoveredMessageId === message._id &&
            message.message_type === 'text' &&
            message.direction !== 'sent' &&
            (message.is_deleted === false ||
              message.is_deleted === 'false' ||
              message.is_deleted === undefined) && (
              <CopilotPredictionButton message={message} />
            )}

          {message.direction === 'sent' && (
            <TooltipComponent
              content={
                <span>
                  {getOwnerDetails(message.user_id, 'fullname', 'sms')}
                </span>
              }
              delayDuration='100'
            >
              <div>
                <Avatar>
                  <AvatarImage
                    src={getOwnerDetails(message.user_id, 'image', 'sms')}
                  />
                  <AvatarFallback>
                    {getOwnerDetails(message.user_id, 'initials', 'sms')}
                  </AvatarFallback>
                </Avatar>
              </div>
            </TooltipComponent>
          )}
        </div>
      </div>
    );
  };

  // Render an email thread item
  const renderEmailThread = (thread) => {
    // Defensive check: ensure thread.messages exists and is an array
    const threadMessages = Array.isArray(thread.messages) ? thread.messages : [];

    // Get unique sender avatars for thread header
    const uniqueSenders = Array.from(
      new Map(
        threadMessages
          .filter((msg) => msg.direction === 'received')
          .map((msg) => [
            msg.sender_email,
            {
              name: msg.sender_name || fullname,
              email: msg.sender_email,
              avatar: profile_picture_url,
            },
          ])
      ).values()
    );

    const previewText = thread.content_text;

    // Format timestamp
    const formattedTimestamp = thread.latest_timestamp
      ? new Date(thread.latest_timestamp).toLocaleString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          month: 'short',
          day: 'numeric',
          weekday: 'short',
        })
      : '';

    return (
      <div key={thread.thread_id} className='m-3'>
        <Accordion type='multiple' className='space-y-3'>
          <AccordionItem
            value={thread.thread_id}
            className='rounded-lg border-none bg-card'
          >
            <AccordionTrigger
              className='h-12 rounded-lg bg-secondary/50 px-3 py-2 hover:bg-secondary hover:no-underline data-[state=open]:rounded-b-none'
              openIcon={NullIcon}
              closeIcon={NullIcon}
            >
              <div className='flex w-full min-w-0 items-center gap-3'>
                {/* Sender Avatars - Overlapping */}
                <div className='flex shrink-0 -space-x-2'>
                  {uniqueSenders.slice(0, 2).map((sender, idx) => (
                    <Avatar
                      key={idx}
                      className='h-8 w-8 border-2 border-background'
                    >
                      {sender.avatar ? (
                        <AvatarImage src={sender.avatar} />
                      ) : (
                        <AvatarFallback className='bg-muted text-[8px] text-foreground'>
                          {sender.name?.charAt(0)?.toUpperCase() || '?'}
                        </AvatarFallback>
                      )}
                    </Avatar>
                  ))}
                  {uniqueSenders.length > 2 && (
                    <div className='flex h-10 w-10 items-center justify-center rounded-full border-2 border-background bg-muted text-xs text-foreground'>
                      +{uniqueSenders.length - 2}
                    </div>
                  )}
                </div>

                {/* Thread Content */}
                <div className='flex min-w-0 flex-1 items-center gap-2 overflow-hidden'>
                  <span className='shrink-0 truncate text-sm font-semibold text-foreground'>
                    {thread.subject || 'No Subject'}
                  </span>
                  <span className='w-52 min-w-0 flex-1 truncate text-left text-sm text-muted-foreground'>
                    {previewText}
                  </span>
                </div>

                {/* Message Count Badge */}
                {thread.message_count > 0 && (
                  <Badge
                    variant='secondary'
                    className='shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-normal text-foreground'
                  >
                    {thread.message_count}
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className='rounded-b-lg bg-secondary/50 px-0 pb-4'>
              <div className='px-3 pt-2'>
                {/* Messages within thread */}
                {threadMessages.map((message, msgIndex) => {
                  const isLastMessage =
                    msgIndex === threadMessages.length - 1;

                  // Map email message fields to expected structure
                  const mappedMessage = {
                    ...message,
                    text: message.content_text || message.content || '',
                    content_html: message.content,
                    message_type:
                      message.attachments?.length > 0 ? 'image' : 'text',
                    channel: 'email',
                  };

                  const senderName =
                    message.direction === 'sent'
                      ? getOwnerDetails(message.user_id, 'fullname', 'email')
                      : message.sender_name || fullname;

                  const senderEmail = message.sender_email;

                  const msgFormattedTimestamp = new Date(
                    message.timestamp
                  ).toLocaleString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    month: 'short',
                    day: 'numeric',
                    weekday: 'short',
                  });

                  // Get preview text for collapsed state
                  const previewMessageText = (
                    message.content_text ||
                    message.content ||
                    ''
                  )
                    .replace(/<[^>]*>/g, '')
                    .substring(0, 100);

                  return (
                    <CollapsibleEmailMessage
                      key={message._id}
                      message={message}
                      mappedMessage={mappedMessage}
                      senderName={senderName}
                      senderEmail={senderEmail}
                      formattedTimestamp={msgFormattedTimestamp}
                      previewText={previewMessageText}
                      isLastMessage={isLastMessage}
                      profile_picture_url={profile_picture_url}
                      defaultPic={defaultPic}
                      fullname={fullname}
                      getOwnerDetails={getOwnerDetails}
                      setHoveredMessageId={setHoveredMessageId}
                    />
                  );
                })}

                {/* Reply Button at the bottom right */}
                <div className='flex justify-end px-3 pb-2 pt-4'>
                  <Button
                    variant='default'
                    size='xs'
                    onClick={() =>
                      onReplyToThread(
                        thread.thread_id,
                        thread.subject,
                        previewText
                      )
                    }
                  >
                    <ReplyIcon className='mr-2 h-4 w-4' />
                    Reply to thread
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        {/* Timestamp below accordion item */}
        {formattedTimestamp && (
          <div className='flex justify-end py-1'>
            <span className='text-xs text-muted-foreground'>
              {formattedTimestamp}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {messages.map((item, index) => {
        // Handle activity log messages (e.g., contact_owner_changed)
        if (item.message_type === 'activity_log') {
          return <ActivityLogMessage key={item._id} message={item} />;
        }

        if (item.thread_id) {
          // Email thread (has thread_id)
          return renderEmailThread(item);
        } else {
          // Regular message (no thread_id)
          return renderRegularMessage(item, index, messages);
        }
      })}
    </>
  );
}

